import { useTranslations } from '@vocab/react';
import {
  MenuItem,
  Button,
  MenuRenderer,
  IconArrow,
  TooltipRenderer,
  Text,
  Box,
  Strong,
} from 'braid-design-system';
import type { FC } from 'react';

import { BUCKETS } from 'src/stores/JobApplicationStore/types';
import mapBucketTypeToBuckets from 'src/stores/JobApplicationStore/utils/mapBucketToBuckets';
import useBucketLabel from 'src/utils/hooks/useBucketLabel';

import translations from '../.vocab';
import type { JobApplicationCardProps } from '../JobApplicationCard';

export const MoveStatusMenu: FC<
  Pick<
    JobApplicationCardProps,
    'currentBucket' | 'jobApplication' | 'changeStatus'
  >
> = ({ currentBucket, jobApplication, changeStatus }) => {
  const { t } = useTranslations(translations);
  const { getLabelFromBucket } = useBucketLabel();

  const menuItems = [
    { bucket: BUCKETS.INBOX, label: t('Inbox') },
    { bucket: BUCKETS.PRESCREEN, label: t('Prescreen') },
    { bucket: BUCKETS.SHORTLIST, label: t('Shortlist') },
    { bucket: BUCKETS.INTERVIEW, label: t('Interview') },
    { bucket: BUCKETS.OFFER, label: t('Offer') },
    { bucket: BUCKETS.ACCEPT, label: t('Accept') },
    {
      bucket: BUCKETS.NOT_SUITABLE,
      label: t('Not Suitable'),
    },
  ].map(({ bucket, label }) => (
    <MenuItem
      key={bucket}
      onClick={() => {
        if (currentBucket !== bucket) {
          changeStatus(bucket);
        }
      }}
    >
      {currentBucket === bucket ? <Strong>{label}</Strong> : label}
    </MenuItem>
  ));

  if (currentBucket === BUCKETS.INBOX) {
    return (
      <MenuRenderer
        offsetSpace="small"
        width="small"
        trigger={(triggerProps) => (
          <TooltipRenderer
            id="card-move-status-menu"
            tooltip={<Text>{t('Move to')}</Text>}
          >
            {({ triggerProps: tooltipTriggerProps }) => (
              <Box {...tooltipTriggerProps}>
                <Button
                  {...triggerProps}
                  id={`${jobApplication.id}-move-to`}
                  size="small"
                  tone="neutral"
                  variant="soft"
                  aria-label={t('Move fullName to a status', {
                    fullName: jobApplication.fullName,
                  })}
                >
                  <IconArrow direction="right" />
                </Button>
              </Box>
            )}
          </TooltipRenderer>
        )}
      >
        {menuItems}
      </MenuRenderer>
    );
  }

  return (
    <MenuRenderer
      offsetSpace="small"
      width="small"
      align="right"
      trigger={(triggerProps) => (
        <Button
          {...triggerProps}
          tone="neutral"
          variant="solid"
          iconPosition="trailing"
          size="small"
          aria-label={t('Move fullName from bucket', {
            fullName: jobApplication.fullName,
            bucket: getLabelFromBucket(mapBucketTypeToBuckets(currentBucket)),
          })}
          icon={<IconArrow direction="right" />}
        >
          {t('Move to')}
        </Button>
      )}
    >
      {menuItems}
    </MenuRenderer>
  );
};
